/**
 * OutreachStatusPill — status is always DERIVED, never a raw enum render.
 * `syncing` (the roster projection is behind the write) takes priority over
 * everything else. The label comes from `outreachState` while the roster status
 * is `outreach`, and from the roster status once the row has left outreach (with
 * the brand, removed); a roster status this component has never heard of falls
 * back to a de-slugged label instead of crashing.
 */
import { cn } from '../../../lib/utils';
import type { OutreachIntent, OutreachRow, OutreachState } from '@discovr/types';
import { INTENT_LABELS } from './IntentPill';

const STOPPED_CLS = 'bg-stone-200 text-stone-600';

/** Exhaustive on purpose: a new outreach state fails to compile here instead of rendering a blank pill. */
const STATE_STYLES: Record<OutreachState, { label: string; cls: string }> = {
  // Stone/200, not Stone/100: a selected inbox row is Stone/100 (bg-muted) and would swallow the pill.
  sourced: { label: 'Sourced', cls: 'bg-[var(--stone-200)] text-(--foreground-secondary-default)' },
  contacted: { label: 'Contacted', cls: 'bg-[var(--brand-100)] text-[var(--brand-600)]' },
  replied: { label: 'Replied', cls: 'bg-amber-100 text-[var(--foreground-warning-default)]' },
  rate_provided: { label: 'Rate agreed', cls: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Rejected', cls: STOPPED_CLS },
  not_interested: { label: 'Not interested', cls: STOPPED_CLS },
  ghosted: { label: 'Ghosted', cls: STOPPED_CLS },
  bounced: { label: 'Bounced', cls: 'bg-rose-100 text-rose-700' },
  unsubscribed: { label: 'Unsubscribed', cls: 'bg-rose-100 text-rose-700' },
  removed: { label: 'Removed', cls: 'bg-rose-100 text-rose-700' },
};

const WITH_BRAND_CLS = 'bg-emerald-100 text-emerald-700';
const OUT_OF_PLAY_CLS = 'bg-stone-200 text-stone-600';

const ROSTER_STATUS_STYLES: Partial<Record<OutreachRow['status'], { label: string; cls: string }>> = {
  pending: { label: 'With brand', cls: WITH_BRAND_CLS },
  accepted_by_brand: { label: 'Accepted by brand', cls: WITH_BRAND_CLS },
  accepted_by_admin: { label: 'Accepted', cls: WITH_BRAND_CLS },
  accepted_by_creator: { label: 'Accepted by creator', cls: WITH_BRAND_CLS },
  script_pending: { label: 'Script pending', cls: WITH_BRAND_CLS },
  script_review: { label: 'Script in review', cls: WITH_BRAND_CLS },
  script_approved: { label: 'Script approved', cls: WITH_BRAND_CLS },
  content_pending: { label: 'Content pending', cls: WITH_BRAND_CLS },
  content_review: { label: 'Content in review', cls: WITH_BRAND_CLS },
  content_approved: { label: 'Content approved', cls: WITH_BRAND_CLS },
  live_pending: { label: 'Going live', cls: WITH_BRAND_CLS },
  live: { label: 'Live', cls: WITH_BRAND_CLS },
  rejected_by_brand: { label: 'Rejected by brand', cls: OUT_OF_PLAY_CLS },
  rejected_by_creator: { label: 'Declined', cls: OUT_OF_PLAY_CLS },
  rejected_by_admin: { label: 'Rejected', cls: OUT_OF_PLAY_CLS },
  removed_by_admin: { label: 'Removed', cls: 'bg-rose-100 text-rose-700' },
};

// Figma 505:49954 Pill: 8px radius, 4px/8px padding, 12px medium.
export const PILL_BASE = 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1 text-[12px] font-medium';

function deSlug(value: string): string {
  const words = value.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const UNKNOWN_STYLE = { label: 'Unknown', cls: OUT_OF_PLAY_CLS };

type PillRow = Pick<OutreachRow, 'status' | 'outreachState'>;

/** Outreach owns the label while the roster status is `outreach`; after the hand-off the roster status speaks. */
function styleFor({ status, outreachState }: PillRow): { label: string; cls: string } {
  // Defensive: the wire boundary validates, but a row built elsewhere must never render a blank or throwing pill.
  if (typeof status !== 'string' || !status.trim()) return UNKNOWN_STYLE;
  if (status === 'outreach') return STATE_STYLES[outreachState] ?? UNKNOWN_STYLE;
  // A status this build does not know yet defaults to With brand (owner ruling 2026-10-09).
  const known = Object.hasOwn(ROSTER_STATUS_STYLES, status) ? ROSTER_STATUS_STYLES[status] : undefined;
  return known ?? { label: deSlug(status), cls: WITH_BRAND_CLS };
}

/** One label source for the pill and for plain-text consumers (Task 42's attach picker). */
export function outreachStatusLabel(row: PillRow, sourcedLabel?: string): string {
  if (row.status === 'outreach' && row.outreachState === 'sourced' && sourcedLabel) return sourcedLabel;
  return styleFor(row).label;
}

export interface OutreachStatusPillProps {
  row: PillRow;
  intent?: OutreachIntent;
  syncing: boolean;
  /** Row-pills context (ruling 32) wants "Not contacted" instead of "Sourced". */
  sourcedLabel?: string;
}

export function OutreachStatusPill({ row, intent, syncing, sourcedLabel }: OutreachStatusPillProps) {
  if (syncing) {
    return <span className={cn(PILL_BASE, 'bg-[var(--stone-200)] text-(--foreground-secondary-default)')}>Syncing…</span>;
  }

  const { cls } = styleFor(row);
  const label = outreachStatusLabel(row, sourcedLabel);
  const displayLabel = row.status === 'outreach' && row.outreachState === 'replied' && intent ? `${label} · ${INTENT_LABELS[intent]}` : label;

  return <span className={cn(PILL_BASE, cls)}>{displayLabel}</span>;
}
